import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Shield, LogOut, Users, Plus, Copy, Loader2, KeyRound } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { createClass, getMyClasses, joinClass, getClassMembers } from '../lib/api';
import { DemoChip } from './ui/demo-chip';

export const Settings = () => {
  const { profile, isTeacher, signOut, isDemo } = useAuth();
  const [myClasses, setMyClasses] = useState([]);
  const [classMemberCounts, setClassMemberCounts] = useState({});
  const [newClassName, setNewClassName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);

  const loadClasses = useCallback(async () => {
    setIsLoadingClasses(true);
    try {
      const data = await getMyClasses();
      const classes = data.classes || [];
      setMyClasses(classes);

      // For teachers, fetch member counts
      if (isTeacher) {
        const counts = {};
        for (const cls of classes) {
          try {
            const members = await getClassMembers(cls.join_code);
            counts[cls.join_code] = members.count || 0;
          } catch {
            counts[cls.join_code] = 0;
          }
        }
        setClassMemberCounts(counts);
      }
    } catch (error) {
      console.error('Failed to load classes:', error);
    } finally {
      setIsLoadingClasses(false);
    }
  }, [isTeacher]);

  useEffect(() => {
    if (profile) {
      loadClasses();
    }
  }, [profile, loadClasses]);

  const handleCreateClass = async () => {
    if (isDemo) {
      toast.info('Demo changes not saved');
      return;
    }
    const name = newClassName.trim() || 'My Class';
    setIsCreating(true);
    try {
      const result = await createClass(name);
      toast.success(`Class created! Join code: ${result.join_code}`);
      setNewClassName('');
      await loadClasses();
    } catch (error) {
      toast.error('Failed to create class. Please try again.');
      console.error('Create class error:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinClass = async () => {
    if (!joinCode.trim()) {
      toast.error('Please enter a class code.');
      return;
    }
    setIsJoining(true);
    try {
      const result = await joinClass(joinCode.trim().toUpperCase());
      if (result.status === 'already_joined') {
        toast.info(`You're already in ${result.class_name}.`);
      } else {
        toast.success(`Joined ${result.class_name}!`);
      }
      setJoinCode('');
      await loadClasses();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Invalid class code. Check with your teacher.');
      } else {
        toast.error('Failed to join class. Please try again.');
      }
      console.error('Join class error:', error);
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-white">Settings</h2>
        <p className="text-slate-400 mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Info */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-surface p-6"
      >
        <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <User className="text-teal-flow" size={20} />
          Profile
        </h3>
        <div className="flex items-center gap-6">
          <img
            src={profile?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile?.email}`}
            alt="Avatar"
            className="w-16 h-16 rounded-full bg-slate-700"
          />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User size={14} className="text-slate-500" />
              <span className="text-white">{profile?.full_name || 'Unknown'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-slate-500" />
              <span className="text-slate-400 text-sm">{profile?.email || 'No email'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield size={14} className="text-slate-500" />
              <span className={`status-chip ${
                isTeacher ? 'status-chip--primary' : 'status-chip--gold'
              }`}>
                {profile?.role === 'teacher' ? 'Teacher' : 'Student'}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Class Management — Teacher */}
      {isTeacher && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-surface p-6"
        >
          <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="text-teal-flow" size={20} />
            My Classes
          </h3>

          {/* Create new class */}
          <div className="flex gap-3 mb-4">
            <input
              data-testid="class-name-input"
              type="text"
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateClass()}
              placeholder="Class name (e.g., Period 3 ELA)"
              className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-flow-blue focus:border-transparent"
            />
            <DemoChip label="Sign in to create a class">
              <button
                data-testid="create-class-btn"
                onClick={handleCreateClass}
                disabled={isCreating}
                className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {isCreating ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Plus size={16} />
                )}
                Create Class
              </button>
            </DemoChip>
          </div>

          {/* Class list */}
          {isLoadingClasses ? (
            <div className="flex items-center gap-2 text-slate-400 py-4">
              <Loader2 size={16} className="animate-spin" />
              Loading classes...
            </div>
          ) : myClasses.length === 0 ? (
            <p className="text-slate-500 text-sm py-4">No classes yet. Create one above to get started.</p>
          ) : (
            <div className="space-y-2">
              {myClasses.map((cls) => (
                <div key={cls.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{cls.class_name}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-slate-400 text-sm">
                        Join Code: <span className="font-mono text-teal-flow select-all">{cls.join_code}</span>
                      </span>
                      <span className="text-slate-500 text-xs">
                        {classMemberCounts[cls.join_code] ?? '...'} {classMemberCounts[cls.join_code] === 1 ? 'student' : 'students'}
                      </span>
                    </div>
                  </div>
                  <button
                    data-testid={`copy-code-${cls.join_code}`}
                    onClick={() => {
                      navigator.clipboard.writeText(cls.join_code);
                      toast.success('Join code copied!');
                    }}
                    className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition-colors"
                    title="Copy join code"
                  >
                    <Copy size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Class Management — Student */}
      {!isTeacher && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-surface p-6"
        >
          <h3 className="font-heading text-lg font-semibold text-white mb-2 flex items-center gap-2">
            <KeyRound className="text-gold-bright" size={20} />
            Join a Class
          </h3>
          <p className="text-slate-400 text-sm mb-4">
            Enter the class code from your teacher to join their class.
          </p>
          <div className="flex gap-3 mb-4">
            <input
              data-testid="join-code-input"
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleJoinClass()}
              placeholder="Enter class code"
              maxLength={6}
              className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder:text-slate-500 font-mono text-lg tracking-widest uppercase focus:ring-2 focus:ring-flow-blue focus:border-transparent"
            />
            <button
              data-testid="join-class-btn"
              onClick={handleJoinClass}
              disabled={isJoining}
              className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              {isJoining ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                'Join'
              )}
            </button>
          </div>

          {/* Joined classes */}
          {isLoadingClasses ? (
            <div className="flex items-center gap-2 text-slate-400 py-2">
              <Loader2 size={16} className="animate-spin" />
              Loading classes...
            </div>
          ) : myClasses.length > 0 && (
            <div className="mt-4">
              <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">My Classes</p>
              <div className="space-y-2">
                {myClasses.map((cls) => (
                  <div key={cls.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                    <div>
                      <p className="text-white font-medium">{cls.class_name}</p>
                      <p className="text-slate-500 text-xs font-mono">{cls.join_code}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Sign Out */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card-surface p-6"
      >
        <h3 className="font-heading text-lg font-semibold text-white mb-2">Account</h3>
        <p className="text-slate-400 text-sm mb-4">
          Sign out of your DraftFlow account. Your drafts and progress are saved automatically.
        </p>
        <button
          data-testid="settings-sign-out-btn"
          onClick={signOut}
          className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </motion.div>
    </div>
  );
};

export default Settings;
